import React, { useState, useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { DisabledButton, PrimaryButton } from 'components/common/buttons';
import { useNotification } from 'context/notification';
import { buSelector } from 'redux/business-units/business-units-selector';
import { ppSelector } from 'redux/product-platforms/product-platforms-selector';
import { userContext } from 'context/user';
import { editProgram } from 'services/programs';
import { styles } from './styles';
import { setActiveProgram } from 'redux/actions/program';
import DOMPurify from 'dompurify';
import { rules } from '../../../../util/rule-name-convention';

const RendererInput = (value, label, params) => {
  return (
    <TextField
      {...params}
      value={value}
      required
      label={label}
      variant="outlined"
      size="small"
    />
  );
};

export default function EditProgram({ updateProgramDetails }) {
  const classes = styles();
  const dispatch = useDispatch();
  const { dispatchNotification } = useNotification();
  const activeProgram = useSelector(
    (state) => state.programReducer.activeProgram,
  );
  const allPrograma = useSelector((state) => state.programReducer.allPrograms);
  const businessUnits = useSelector(buSelector);
  const productPlatforms = useSelector(ppSelector);

  const [selectedProgramName, setSelectedProgramName] = useState(
    activeProgram.programName ?? '',
  );
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState(
    businessUnits.find((bu) => bu.id === activeProgram.businessUnitId) ?? {},
  );
  const [selectedBusinessUnitInput, setSelectedBusinessUnitInput] = useState(
    selectedBusinessUnit.name ?? '',
  );
  const [selectedProductPlatform, setSelectedProductPlatform] = useState(
    productPlatforms.find((pp) => pp.id === activeProgram.productPlatformId) ??
      {},
  );
  const [selectedProductPlatformInput, setSelectedProductPlatformInput] =
    useState(selectedProductPlatform.name ?? '');
  const [errorProgramName, setErrorProgramName] = useState();
  const [programsNameList, setProgramsNameList] = useState([]);

  const { currentUser } = useContext(userContext);

  useEffect(() => {
    setProgramsNameList(() => {
      const newProgramsNameList = allPrograma.map((ap) =>
        ap.programName.toLowerCase(),
      );
      return newProgramsNameList;
    });
  }, [allPrograma]);

  const handleProgramNameChange = useCallback(
    (e) => {
      const sanitizedName = DOMPurify.sanitize(e.target.value);
      const errors = [];
      for (const rule of rules) {
        if (!rule.isSatisfied(sanitizedName)) {
          errors.push(rule.desc);
        }
      }

      if (programsNameList.includes(sanitizedName.toLowerCase())) {
        errors.push('Program name already exists');
      }
      // Update error state if any validation fails
      if (errors.length > 0) {
        setErrorProgramName(errors.join(' ')); // Combine all error messages
      } else {
        setErrorProgramName(null);
      }
      setSelectedProgramName(sanitizedName);
    },
    [programsNameList],
  );

  const handleProductPlatformChange = (e, selectedOption) => {
    setSelectedProductPlatform(selectedOption || {});
    setSelectedProductPlatformInput(selectedOption?.name ?? '');
  };

  const handleBusinessUnitChange = (e, selectedOption) => {
    setSelectedBusinessUnit(selectedOption || {});
    setSelectedBusinessUnitInput(selectedOption?.name ?? '');
  };

  const handleEditProgramClick = useCallback(
    (e) => {
      let isValidated = e.currentTarget.form.reportValidity();
      if (isValidated) {
        const editProgramDetails = {
          programId: activeProgram.programId,
          userId: currentUser.userId,
          programName: selectedProgramName,
          businessUnitId: selectedBusinessUnit.id,
          productPlatformId: selectedProductPlatform.id,
          gitProgram: activeProgram.gitProgram,
        };

        editProgram(editProgramDetails).then((res) => {
          if (res.status === 'success') {
            dispatch(
              setActiveProgram({
                programName: selectedProgramName,
                businessUnitId: selectedBusinessUnit.id,
                productPlatformId: selectedProductPlatform.id,
              }),
            );
            updateProgramDetails();
            dispatchNotification({
              show: true,
              severity: 'success',
              message: `Program updated successfully`,
            });
          } else {
            dispatchNotification({
              show: true,
              severity: 'error',
              message: `${res.error_message}`,
            });
          }
        });
      }
    },
    [
      activeProgram,
      currentUser.userId,
      selectedProgramName,
      selectedBusinessUnit,
      selectedProductPlatform,
    ],
  );

  return (
    <div className={classes.root}>
      <form className={classes.root} noValidate>
        <TextField
          required
          className={classes.editProgramInput}
          label="Program Name"
          style={{ width: '100%' }}
          variant="outlined"
          size="small"
          value={selectedProgramName}
          onChange={handleProgramNameChange}
          error={errorProgramName}
        />
        {errorProgramName && (
          <p className={classes.programNameInputValidation}>
            {errorProgramName}
          </p>
        )}
        <Autocomplete
          value={selectedProductPlatform}
          getOptionLabel={(option) => option?.name || ''}
          className={classes.editProgramInput}
          onChange={(e, val) => handleProductPlatformChange(e, val)}
          renderInput={RendererInput.bind(
            null,
            selectedProductPlatformInput,
            'Product Platform',
          )}
          options={productPlatforms}
        />
        <Autocomplete
          className={classes.editProgramInput}
          getOptionLabel={(option) => option?.name || ''}
          value={selectedBusinessUnit}
          onChange={(e, val) => handleBusinessUnitChange(e, val)}
          renderInput={RendererInput.bind(
            null,
            selectedBusinessUnitInput,
            'Business Unit',
          )}
          options={businessUnits}
        />
        {selectedProgramName !== activeProgram.programName ||
        selectedBusinessUnit.id !== activeProgram.businessUnitId ||
        selectedProductPlatform.id !== activeProgram.productPlatformId ? (
          <PrimaryButton onClick={handleEditProgramClick}>Save</PrimaryButton>
        ) : (
          <DisabledButton>Save</DisabledButton>
        )}
      </form>
    </div>
  );
}
